/**
 * Provider connection testing utilities
 * Tests connectivity to various LLM providers
 */

import * as p from "@clack/prompts";
// @ts-expect-error picocolors has no type declarations
import color from "picocolors";

/**
 * Connection test result
 */
export interface ConnectionTestResult {
  success: boolean;
  latencyMs: number;
  error?: string;
  models?: string[];
}

/**
 * Test Anthropic API connection
 * Uses subscription passthrough - tests if Claude Max subscription is active
 */
export async function testAnthropicConnection(
  baseUrl: string = "https://api.anthropic.com",
  apiKey?: string
): Promise<ConnectionTestResult> {
  const startTime = Date.now();

  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "anthropic-version": "2023-06-01",
    };

    if (apiKey) {
      headers["x-api-key"] = apiKey;
    }

    // Simple test request with minimal tokens
    const response = await fetch(`${baseUrl}/v1/messages`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        model: "claude-sonnet-4-20250514",
        max_tokens: 1,
        messages: [{ role: "user", content: "Hi" }],
      }),
      signal: AbortSignal.timeout(10000),
    });

    const latencyMs = Date.now() - startTime;

    if (response.ok) {
      return { success: true, latencyMs };
    }

    // Check for specific error types
    await response.text(); // Consume body
    let errorMessage = `HTTP ${response.status}`;

    if (response.status === 401) {
      errorMessage = "Authentication failed - invalid or missing API key";
    } else if (response.status === 403) {
      errorMessage = "Access denied - check subscription status";
    } else if (response.status === 429) {
      errorMessage = "Rate limited - too many requests";
    } else if (response.status >= 500) {
      errorMessage = "Anthropic server error - try again later";
    }

    return { success: false, latencyMs, error: errorMessage };
  } catch (error) {
    const latencyMs = Date.now() - startTime;
    let errorMessage = "Unknown error";

    if (error instanceof Error) {
      if (error.name === "AbortError" || error.message.includes("timeout")) {
        errorMessage = "Connection timeout - server not responding";
      } else if (error.message.includes("ECONNREFUSED")) {
        errorMessage = "Connection refused - server unreachable";
      } else if (error.message.includes("ENOTFOUND")) {
        errorMessage = "DNS lookup failed - invalid URL";
      } else {
        errorMessage = error.message;
      }
    }

    return { success: false, latencyMs, error: errorMessage };
  }
}

/**
 * Test Z.AI API connection
 */
export async function testZaiConnection(
  apiKey: string,
  baseUrl: string = "https://api.z.ai/api/anthropic"
): Promise<ConnectionTestResult> {
  const startTime = Date.now();

  try {
    const response = await fetch(`${baseUrl}/v1/messages`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "anthropic-version": "2023-06-01",
        "x-api-key": apiKey,
        "ANTHROPIC_AUTH_TOKEN": apiKey,
      },
      body: JSON.stringify({
        model: "GLM-4.7",
        max_tokens: 1,
        messages: [{ role: "user", content: "Hi" }],
      }),
      signal: AbortSignal.timeout(15000),
    });

    const latencyMs = Date.now() - startTime;

    if (response.ok) {
      return { success: true, latencyMs };
    }

    await response.text(); // Consume body
    let errorMessage = `HTTP ${response.status}`;

    if (response.status === 401 || response.status === 403) {
      errorMessage = "Invalid API key - check your Z.AI dashboard";
    } else if (response.status === 429) {
      errorMessage = "Rate limited - quota exceeded";
    } else if (response.status >= 500) {
      errorMessage = "Z.AI server error - try again later";
    }

    return { success: false, latencyMs, error: errorMessage };
  } catch (error) {
    const latencyMs = Date.now() - startTime;
    let errorMessage = "Unknown error";

    if (error instanceof Error) {
      if (error.name === "AbortError" || error.message.includes("timeout")) {
        errorMessage = "Connection timeout - server not responding";
      } else if (error.message.includes("ECONNREFUSED")) {
        errorMessage = "Connection refused - check Z.AI service status";
      } else {
        errorMessage = error.message;
      }
    }

    return { success: false, latencyMs, error: errorMessage };
  }
}

/**
 * Test Grok (xAI) API connection
 */
export async function testGrokConnection(
  apiKey: string,
  baseUrl: string = "https://api.x.ai"
): Promise<ConnectionTestResult> {
  const startTime = Date.now();

  try {
    const response = await fetch(`${baseUrl}/v1/chat/completions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model: "grok-2",
        max_tokens: 1,
        messages: [{ role: "user", content: "Hi" }],
      }),
      signal: AbortSignal.timeout(15000),
    });

    const latencyMs = Date.now() - startTime;

    if (response.ok) {
      return { success: true, latencyMs };
    }

    await response.text(); // Consume body
    let errorMessage = `HTTP ${response.status}`;

    if (response.status === 401 || response.status === 403) {
      errorMessage = "Invalid API key - check your xAI dashboard";
    } else if (response.status === 429) {
      errorMessage = "Rate limited - quota exceeded";
    } else if (response.status >= 500) {
      errorMessage = "Grok server error - try again later";
    }

    return { success: false, latencyMs, error: errorMessage };
  } catch (error) {
    const latencyMs = Date.now() - startTime;
    let errorMessage = "Unknown error";

    if (error instanceof Error) {
      if (error.name === "AbortError" || error.message.includes("timeout")) {
        errorMessage = "Connection timeout - server not responding";
      } else if (error.message.includes("ECONNREFUSED")) {
        errorMessage = "Connection refused - check xAI service status";
      } else {
        errorMessage = error.message;
      }
    }

    return { success: false, latencyMs, error: errorMessage };
  }
}

/**
 * Test OpenAI API connection
 */
export async function testOpenAIConnection(
  apiKey: string,
  baseUrl: string = "https://api.openai.com/v1"
): Promise<ConnectionTestResult> {
  const startTime = Date.now();

  try {
    const response = await fetch(`${baseUrl}/models`, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${apiKey}`,
      },
      signal: AbortSignal.timeout(10000),
    });

    const latencyMs = Date.now() - startTime;

    if (response.ok) {
      return { success: true, latencyMs };
    }

    await response.text(); // Consume body
    let errorMessage = `HTTP ${response.status}`;

    if (response.status === 401) {
      errorMessage = "Invalid API key - check your OpenAI dashboard";
    } else if (response.status === 429) {
      errorMessage = "Rate limited - quota exceeded";
    } else if (response.status >= 500) {
      errorMessage = "OpenAI server error - try again later";
    }

    return { success: false, latencyMs, error: errorMessage };
  } catch (error) {
    const latencyMs = Date.now() - startTime;
    let errorMessage = "Unknown error";

    if (error instanceof Error) {
      if (error.name === "AbortError" || error.message.includes("timeout")) {
        errorMessage = "Connection timeout - server not responding";
      } else if (error.message.includes("ECONNREFUSED")) {
        errorMessage = "Connection refused - check OpenAI service status";
      } else {
        errorMessage = error.message;
      }
    }

    return { success: false, latencyMs, error: errorMessage };
  }
}

/**
 * Test Google Gemini API connection
 */
export async function testGeminiConnection(
  apiKey: string,
  baseUrl: string = "https://generativelanguage.googleapis.com/v1beta"
): Promise<ConnectionTestResult> {
  const startTime = Date.now();

  try {
    const response = await fetch(`${baseUrl}/models?key=${apiKey}`, {
      method: "GET",
      signal: AbortSignal.timeout(10000),
    });

    const latencyMs = Date.now() - startTime;

    if (response.ok) {
      return { success: true, latencyMs };
    }

    await response.text(); // Consume body
    let errorMessage = `HTTP ${response.status}`;

    if (response.status === 400 || response.status === 403) {
      errorMessage = "Invalid API key - check your Google AI Studio dashboard";
    } else if (response.status === 429) {
      errorMessage = "Rate limited - quota exceeded";
    } else if (response.status >= 500) {
      errorMessage = "Gemini server error - try again later";
    }

    return { success: false, latencyMs, error: errorMessage };
  } catch (error) {
    const latencyMs = Date.now() - startTime;
    let errorMessage = "Unknown error";

    if (error instanceof Error) {
      if (error.name === "AbortError" || error.message.includes("timeout")) {
        errorMessage = "Connection timeout - server not responding";
      } else if (error.message.includes("ECONNREFUSED")) {
        errorMessage = "Connection refused - check Google AI service status";
      } else {
        errorMessage = error.message;
      }
    }

    return { success: false, latencyMs, error: errorMessage };
  }
}

/**
 * Test Mistral AI API connection
 */
export async function testMistralConnection(
  apiKey: string,
  baseUrl: string = "https://api.mistral.ai"
): Promise<ConnectionTestResult> {
  const startTime = Date.now();

  try {
    const response = await fetch(`${baseUrl}/v1/models`, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${apiKey}`,
      },
      signal: AbortSignal.timeout(10000),
    });

    const latencyMs = Date.now() - startTime;

    if (response.ok) {
      return { success: true, latencyMs };
    }

    await response.text(); // Consume body
    let errorMessage = `HTTP ${response.status}`;

    if (response.status === 401) {
      errorMessage = "Invalid API key - check your Mistral dashboard";
    } else if (response.status === 429) {
      errorMessage = "Rate limited - quota exceeded";
    } else if (response.status >= 500) {
      errorMessage = "Mistral server error - try again later";
    }

    return { success: false, latencyMs, error: errorMessage };
  } catch (error) {
    const latencyMs = Date.now() - startTime;
    let errorMessage = "Unknown error";

    if (error instanceof Error) {
      if (error.name === "AbortError" || error.message.includes("timeout")) {
        errorMessage = "Connection timeout - server not responding";
      } else if (error.message.includes("ECONNREFUSED")) {
        errorMessage = "Connection refused - check Mistral service status";
      } else {
        errorMessage = error.message;
      }
    }

    return { success: false, latencyMs, error: errorMessage };
  }
}

/**
 * Test DeepSeek API connection
 */
export async function testDeepSeekConnection(
  apiKey: string,
  baseUrl: string = "https://api.deepseek.com"
): Promise<ConnectionTestResult> {
  const startTime = Date.now();

  try {
    const response = await fetch(`${baseUrl}/v1/models`, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${apiKey}`,
      },
      signal: AbortSignal.timeout(10000),
    });

    const latencyMs = Date.now() - startTime;

    if (response.ok) {
      return { success: true, latencyMs };
    }

    await response.text(); // Consume body
    let errorMessage = `HTTP ${response.status}`;

    if (response.status === 401) {
      errorMessage = "Invalid API key - check your DeepSeek dashboard";
    } else if (response.status === 429) {
      errorMessage = "Rate limited - quota exceeded";
    } else if (response.status >= 500) {
      errorMessage = "DeepSeek server error - try again later";
    }

    return { success: false, latencyMs, error: errorMessage };
  } catch (error) {
    const latencyMs = Date.now() - startTime;
    let errorMessage = "Unknown error";

    if (error instanceof Error) {
      if (error.name === "AbortError" || error.message.includes("timeout")) {
        errorMessage = "Connection timeout - server not responding";
      } else if (error.message.includes("ECONNREFUSED")) {
        errorMessage = "Connection refused - check DeepSeek service status";
      } else {
        errorMessage = error.message;
      }
    }

    return { success: false, latencyMs, error: errorMessage };
  }
}

/**
 * Ollama model info
 */
interface OllamaModelInfo {
  name: string;
  size: number;
  digest: string;
  modified_at: string;
}

/**
 * Test Ollama connection and fetch available models
 */
export async function testOllamaConnection(
  baseUrl: string = "http://localhost:11434"
): Promise<ConnectionTestResult> {
  const startTime = Date.now();

  try {
    const response = await fetch(`${baseUrl}/api/tags`, {
      method: "GET",
      signal: AbortSignal.timeout(5000),
    });

    const latencyMs = Date.now() - startTime;

    if (response.ok) {
      const data = (await response.json()) as { models?: OllamaModelInfo[] };
      const models = data.models?.map((m) => m.name) ?? [];

      return { success: true, latencyMs, models };
    }

    return {
      success: false,
      latencyMs,
      error: `HTTP ${response.status} - Ollama API error`,
    };
  } catch (error) {
    const latencyMs = Date.now() - startTime;
    let errorMessage = "Unknown error";

    if (error instanceof Error) {
      if (error.name === "AbortError" || error.message.includes("timeout")) {
        errorMessage = "Connection timeout - is Ollama running?";
      } else if (error.message.includes("ECONNREFUSED")) {
        errorMessage = "Connection refused - start Ollama with: ollama serve";
      } else {
        errorMessage = error.message;
      }
    }

    return { success: false, latencyMs, error: errorMessage };
  }
}

/**
 * Display spinner while testing connection
 */
export async function testConnectionWithSpinner(
  providerName: string,
  testFn: () => Promise<ConnectionTestResult>
): Promise<ConnectionTestResult> {
  const spinner = p.spinner();
  spinner.start(`Testing ${providerName} connection...`);

  const result = await testFn();

  if (result.success) {
    spinner.stop(
      color.green(`Connected to ${providerName}`) +
        color.dim(` (${result.latencyMs}ms)`)
    );
  } else {
    spinner.stop(color.red(`Failed to connect to ${providerName}`));
    p.log.error(color.dim(result.error ?? "Unknown error"));
  }

  return result;
}

/**
 * Test a provider by name from loaded config
 */
export async function testProvider(name?: string): Promise<void> {
  p.intro(color.bgCyan(color.black(" Provider Connection Test ")));

  if (!name) {
    // Load config and let user select
    const { loadConfig } = await import("../config/index.js");

    let config;
    try {
      config = loadConfig({ useDefaults: true });
    } catch {
      p.log.error("Failed to load configuration");
      p.outro(color.red("Test failed"));
      return;
    }

    const enabledProviders = config.providers.filter((p) => p.enabled);

    if (enabledProviders.length === 0) {
      p.log.warn("No providers configured. Run: ludicrous-gateway setup");
      p.outro(color.yellow("No providers to test"));
      return;
    }

    const selected = await p.select({
      message: "Select provider to test:",
      options: enabledProviders.map((p) => ({
        value: p.name,
        label: `${p.name} (${p.type})`,
      })),
    });

    if (p.isCancel(selected)) {
      p.cancel("Test cancelled");
      return;
    }

    name = selected as string;
  }

  // Test the selected provider
  p.log.info(`Testing provider: ${color.cyan(name)}`);

  // For now, do a generic test based on provider type
  // In a full implementation, we'd load the actual config
  let result: ConnectionTestResult;

  if (name.toLowerCase().includes("anthropic") || name === "claude") {
    result = await testConnectionWithSpinner("Anthropic", () =>
      testAnthropicConnection()
    );
  } else if (name.toLowerCase().includes("zai") || name.toLowerCase().includes("glm")) {
    p.log.warn("Z.AI test requires API key from config");
    result = { success: false, latencyMs: 0, error: "API key required" };
  } else if (name.toLowerCase().includes("ollama")) {
    result = await testConnectionWithSpinner("Ollama", () =>
      testOllamaConnection()
    );
    if (result.success && result.models) {
      p.log.info(`Available models: ${result.models.join(", ")}`);
    }
  } else {
    p.log.warn(`Unknown provider type: ${name}`);
    result = { success: false, latencyMs: 0, error: "Unknown provider" };
  }

  if (result.success) {
    p.outro(color.green("Connection test passed"));
  } else {
    p.outro(color.red("Connection test failed"));
  }
}
